import { useState, useEffect } from 'react';
import { LeadFormStepOne } from './LeadFormStepOne';
import { LeadFormStepTwo } from './LeadFormStepTwo';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DaylitLogo } from '@lendica/components';
import { DaylitTheme } from '@lendica/theme';
import Link from '@mui/material/Link';
import {
    submitLead,
    trackLeadFormStep1,
    trackLeadFormStep2,
    trackLeadFormConversion,
    trackPhoneAuthFromLead,
} from '../api/leads';
import { phone, useAccountContext, setAxiosBaseUrl } from '@lendica/auth';
import type { ExperimentMeta } from '@lendica/auth';
import { useNavigate } from 'react-router-dom';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { FEATURE_FLAGS } from '..';
import { useLeadData } from '../providers/LeadDataProvider';
import { extractAllowedParams, sanitizeParams } from '../config/allowedQueryParams';

// Lead form data interface
export interface LeadFormData {
    goals: string[];
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    phone?: string;
    experiments?: ExperimentMeta[];
    // Query parameters that may be captured
    [key: string]: any; // Allows for dynamic query params like utm_*, partner_name, etc.
}

export interface LeadFormWizardCompletionContext {
    variant?: string;
    searchParams?: URLSearchParams;
}

export type LeadFormWizardOnComplete = (
    data: LeadFormData,
    context: LeadFormWizardCompletionContext
) => void | Promise<void>;

interface LeadFormWizardProps {
    onComplete?: LeadFormWizardOnComplete;
    searchParams?: URLSearchParams;
    baseURL: string;
    requirePhone?: boolean;
    appType?: 'application' | 'dashboard';
    env?: string;
}

export const LeadFormWizard = ({
    onComplete,
    searchParams,
    baseURL,
    requirePhone = true,
    appType = 'application',
    env = 'prod',
}: LeadFormWizardProps) => {
    const [currentStep, setCurrentStep] = useState(1);
    const initialEmail = (searchParams?.get('email') || '').trim();
    const emailPrefilled = Boolean(initialEmail);
    const [formData, setFormData] = useState<LeadFormData>({
        goals: [],
        email: initialEmail,
        firstName: '',
        lastName: '',
        company: '',
        phone: '',
        experiments: [],
    });

    const { setTempPhoneNumber } = useAccountContext();
    const { setLeadData } = useLeadData();
    const navigate = useNavigate();

    // Split.io hooks for A/B testing
    const { treatments } = useSplitTreatments({ names: [FEATURE_FLAGS.LEAD_FORM_TEST] });

    // Initialize auth axios instance
    useEffect(() => {
        setAxiosBaseUrl(baseURL);
    }, [baseURL]);

    const handleStepOneSubmit = async (values: { goals: string[]; email: string }) => {
        setFormData((prev: LeadFormData) => ({ ...prev, ...values }));

        // Extract and sanitize allowed query parameters
        const allowedParams = sanitizeParams(extractAllowedParams(searchParams));

        // Prepare lead data with allowed parameters
        const leadDataWithParams = {
            ...values,
            ...allowedParams,
        };

        // Submit partial lead data to HubSpot (only goals and email)
        // Use non-blocking submission - don't prevent user from continuing if it fails
        await submitLead(leadDataWithParams, false);

        // Track step 1 completion for A/B test using centralized tracking
        const treatment = treatments[FEATURE_FLAGS.LEAD_FORM_TEST];
        trackLeadFormStep1({
            variant: treatment?.treatment,
            goals: values.goals,
            email: values.email,
        });

        setCurrentStep(2);
    };

    const handleStepTwoSubmit = async (values: {
        email: string;
        firstName: string;
        lastName: string;
        company: string;
        phone?: string;
    }) => {
        const sanitizedPhone = values.phone?.trim();

        const finalData: LeadFormData = {
            ...formData,
            ...values,
            ...(requirePhone || sanitizedPhone ? { phone: sanitizedPhone ?? '' } : {}),
        };

        if (!requirePhone && !sanitizedPhone) {
            delete finalData.phone;
        }

        const experiments = Object.entries(treatments).reduce<ExperimentMeta[]>(
            (acc, [flag, treatment]) => {
                if (!treatment || typeof treatment.treatment !== 'string') {
                    return acc;
                }

                const config = (treatment.config ?? null) as Record<string, unknown> | null;
                let impressionKey: string | null = null;
                if (config && typeof config['impression_key'] === 'string') {
                    impressionKey = config['impression_key'] as string;
                }

                acc.push({
                    feature_flag_name: flag,
                    variant: treatment.treatment,
                    impression_key: impressionKey,
                    recorded_at: new Date().toISOString(),
                    config,
                    source: 'split',
                });

                return acc;
            },
            []
        );

        const finalDataWithExperiments: LeadFormData = experiments.length
            ? { ...finalData, experiments }
            : finalData;

        setFormData(finalDataWithExperiments);

        // Extract and sanitize allowed query parameters
        const allowedParams = sanitizeParams(extractAllowedParams(searchParams));

        // Store complete lead data with allowed parameters in LeadDataProvider for later use
        const leadDataForStorage = {
            ...finalDataWithExperiments,
            ...allowedParams,
        };
        setLeadData(leadDataForStorage);

        const treatment = treatments[FEATURE_FLAGS.LEAD_FORM_TEST];
        try {
            // Prepare final lead data with allowed parameters
            // Note: allowedParams already extracted above for storage
            const finalDataWithParams = {
                ...finalDataWithExperiments,
                ...allowedParams,
            };
            const hasOnComplete = typeof onComplete === 'function';

            // Submit complete lead data to backend/HubSpot
            await submitLead(finalDataWithParams);

            // Track step 2 completion for A/B test using centralized tracking
            trackLeadFormStep2({
                variant: treatment?.treatment,
                email: finalDataWithExperiments.email,
                firstName: finalDataWithExperiments.firstName,
                lastName: finalDataWithExperiments.lastName,
                company: finalDataWithExperiments.company,
                phone: finalDataWithExperiments.phone ?? '',
            });

            if (requirePhone && finalDataWithExperiments.phone) {
                // Store phone number for OTP verification
                setTempPhoneNumber(finalDataWithExperiments.phone);

                await phone(finalDataWithExperiments.phone);

                // Track final conversion for A/B test using centralized tracking
                trackLeadFormConversion(finalDataWithExperiments.email, treatment?.treatment);

                // Track phone auth initiation from lead form using centralized tracking
                trackPhoneAuthFromLead(
                    finalDataWithExperiments.phone,
                    finalDataWithExperiments.email
                );
            } else {
                setTempPhoneNumber(null);
            }

            if (hasOnComplete) {
                await onComplete(finalDataWithParams, {
                    variant: treatment?.treatment,
                    searchParams,
                });
            } else {
                // Redirect to OTP verification with preserved search params
                const preservedParams = searchParams?.toString();
                const queryString = preservedParams
                    ? `?${preservedParams}&variant=${treatment?.treatment}`
                    : `?variant=${treatment?.treatment}`;
                navigate(`/verify-phone${queryString}`);
            }
        } catch (error: any) {
            // You might want to show an error message to the user
            // For now, still call onComplete to allow UI progression
            onComplete?.(finalDataWithExperiments, {
                searchParams,
                variant: treatment?.treatment,
            });
        }
    };

    const handleBack = () => {
        setCurrentStep(1);
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case 1:
                return 'Choose your cash goals to start.';
            case 2:
                return 'Get started with Lendica.';
            default:
                return '';
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <LeadFormStepOne
                        initialValues={{ goals: formData.goals, email: formData.email }}
                        onSubmit={handleStepOneSubmit}
                        emailDisabled={emailPrefilled}
                    />
                );
            case 2:
                return (
                    <LeadFormStepTwo
                        initialValues={{
                            email: formData.email,
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            company: formData.company,
                            phone: requirePhone ? formData.phone ?? '' : undefined,
                        }}
                        onSubmit={handleStepTwoSubmit}
                        onBack={handleBack}
                        requirePhone={requirePhone}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                px: {
                    xs: 2,
                    sm: 3,
                    lg: 4,
                },
                py: {
                    xs: 2,
                    sm: 3,
                    lg: 4,
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                width: { xs: '100%', md: 'fit-content' },
                minWidth: { md: 380 }, // Ensure minimum width to accommodate form
                height: { xs: '100vh', md: 'auto' },
                justifyContent: { xs: 'center', md: 'flex-start' },
            }}
        >
            <Box
                sx={{
                    mb: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <DaylitLogo
                    style={{
                        color: DaylitTheme.Palette.Burgundy,
                        height: 27,
                        width: 'auto',
                        display: 'block',
                    }}
                />
            </Box>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}>
                {getStepTitle()}
            </Typography>
            {renderStep()}
            {appType === 'application' ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Link href="/login">
                            <Typography
                                variant="body2"
                                sx={{ textAlign: 'center', fontWeight: 400 }}
                            >
                                Continue
                            </Typography>
                        </Link>
                        <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 400 }}>
                            with an existing application
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 400 }}>
                            Already have an account with Daylit?
                        </Typography>
                        <Link
                            href={
                                env === 'prod'
                                    ? 'https://app.daylit.com/'
                                    : 'https://portal.app-idev.golendica.com/'
                            }
                        >
                            <Typography
                                variant="body2"
                                sx={{ textAlign: 'center', fontWeight: 400 }}
                            >
                                Sign in
                            </Typography>
                        </Link>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 400 }}>
                        Already have an account?
                    </Typography>
                    <Link href="/auth">
                        <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 400 }}>
                            Sign in
                        </Typography>
                    </Link>
                </Box>
            )}
        </Box>
    );
};
